import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import _ from "lodash";
import { useMutation, useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import { MyNewsItemForm } from "my-news/forms";
import { MY_NEWS_GET_ITEM_FILTER, MY_NEWS_UPDATE_ITEM } from "my-news/graphql";
import { useMyNewsItem } from "my-news/hooks";
import { OcPageNameTitle, OcPageTransition } from "oc/components";
import {
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
  useOcSnackbar,
} from "oc/hooks";

const useHandleEditRoleFilters = ({ newsId }: { newsId: string }) => {
  let { contractCode: contract } = useOcCurrentUserState();
  const { roleFilters, handleAddFilter, handleRemoveFilter, setRoleFilters } =
    useOcRoleFilters({
      contract,
      product: "MyNews",
      role: "MyNewsEditor", // Replace with the correct RoleCategoriesT value
      id: "MyNewsEditEmployeesFilter",
    });

  const [initialRoleFilter, setInitialRoleFilter] = useState();

  const { data } = useQuery(MY_NEWS_GET_ITEM_FILTER, {
    variables: {
      newsId,
    },
    onCompleted: (data) => {
      const filter = data.getMyNewsItemFilter;
      const filterState = { ...filter };
      delete filterState.__typename;
      setInitialRoleFilter(filterState);
      setRoleFilters(filterState);
    },
  });

  const resetRoleFilters = () => {
    const filter = data.getMyNewsItemFilter;
    const filterState = { ...filter };
    delete filterState.__typename;
    if (!_.isEqual(filterState, roleFilters)) {
      setRoleFilters(filterState);
      setInitialRoleFilter(filterState);
    }
  };

  return {
    roleFilters,
    handleAddFilter,
    handleRemoveFilter,
    resetRoleFilters,
    contract,
    initialRoleFilter,
  };
};

export const MyNewsEditPage = () => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  let { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { setOpenMessage } = useOcSnackbar();

  const { data, loading, error } = useMyNewsItem({
    _id: id,
  });

  const {
    roleFilters,
    handleAddFilter,
    handleRemoveFilter,
    contract,
    resetRoleFilters,
    initialRoleFilter,
  } = useHandleEditRoleFilters({ newsId: id });

  const initialValues = {
    title: data?.title || "",
    description: data?.description || "",
    content: data?.content || "",
    validFrom: data?.validFrom ? new Date(data.validFrom) : null,
    validTo: data?.validTo || null,
    priority: data?.priority || "normal",
    publish: false,
    isEdit: true,
  };

  const publishedAt = data?.publishedAt ? data?.publishedAt : null;

  const [updateMyNewsItem] = useMutation(MY_NEWS_UPDATE_ITEM);

  type MyNewsItemFormValues = {
    title: string;
    description: string;
    content: string;
    validFrom: Date | null;
    validTo: Date | null;
    priority: string;
    publish: boolean;
    isEdit: boolean;
    // Add any other fields expected from the form here
  };

  const handleSubmit = (values: MyNewsItemFormValues) => {
    const params = { ...values, roleFilters };
    updateMyNewsItem({
      variables: {
        _id: id,
        ...params,
      },
      onCompleted: () => {
        history.goBack();
        setOpenMessage({
          type: "success",
          message: t("OC_OPERATION_SUCCESSFUL"),
        });
      },
      onError: (error) => {
        console.log({ error });
        setOpenMessage({
          type: "error",
          message: t("OC_OPERATION_UNSUCCESSFUL"),
        });
      },
    });
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle title={t("MY_NEWS_EDIT_MY_NEWS_ITEM")} goBack={false} />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <MyNewsItemForm
          cardLabel={t("MY_NEWS_EDIT_MY_NEWS_ITEM")}
          initialValues={initialValues}
          initialRoleFilter={initialRoleFilter}
          handleSubmit={handleSubmit}
          roleFilters={roleFilters}
          handleAddFilter={handleAddFilter}
          handleRemoveFilter={handleRemoveFilter}
          resetRoleFilters={resetRoleFilters}
          contract={contract}
          loading={loading}
          error={error}
          enableReinitialize={true}
          publishedAt={publishedAt}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyNewsEditPage;
